import {useEffect, useRef, useState} from 'react';
import {Button, Popover} from '@ezcater/tapas';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import {useStoreNextRouter} from '@/components/global/providers/StoreNextRouter';
import TextField from '@/components/ui/TextField';
import {useEventBarV2} from '../EventBarV2Provider';
import {useCommitEventBarFulfillment} from '../hooks/useCommitEventBarFulfillment';
import {useEventBarV2Tracking} from '../hooks/useEventBarV2Tracking';
import useSyncDietaryWithFilters from '../hooks/useSyncDietaryWithFilters';
import {focusAdjacentTabbable} from '../utils/fields';
import DietaryFilters from './DietaryFilters';
import EventName from './EventName';
import GroupSizeSelector from './GroupSizeSelector';

type PopoverOpenChange = NonNullable<React.ComponentProps<typeof Popover.Root>['onOpenChange']>;

type WhoProps = {
  isCompressed?: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  onSearch: () => void;
  onUpdateApplied?: () => void;
};

const Who: React.FC<WhoProps> = ({
  isCompressed = false,
  isOpen,
  setIsOpen,
  onSearch,
  onUpdateApplied,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const {t} = useTranslation('app-bar');
  const {headCount} = useEventBarV2();
  const {isOnMenuPage} = useStoreNextRouter();
  const {commitFulfillment} = useCommitEventBarFulfillment();
  const {trackWhoUpdate} = useEventBarV2Tracking();

  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLLabelElement>(null);
  const restoringFocusRef = useRef(false);

  const idSuffix = isCompressed ? 'compressed' : 'expanded';
  const containerId = `who-container-${idSuffix}`;
  const inputId = `who-input-${idSuffix}`;
  const panelId = `who-panel-${idSuffix}`;

  useSyncDietaryWithFilters();

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Who is the last field, so Enter runs the search (same as the Search button).
    if (event.key === 'Enter') {
      event.preventDefault();
      onSearch();
      return;
    }
    // While the panel is open, Base UI's popup focus guards have no trigger to hand focus back to,
    // so Tab would dead-end inside the portal. Move to the next field ourselves — the panel itself
    // is entered with ArrowDown below. Only suppress the keystroke if focus actually moved, so a
    // field with no adjacent tabbable falls through to the browser's own Tab rather than stranding
    // focus on a closed panel.
    if (event.key === 'Tab' && isOpen) {
      setIsOpen(false);
      if (focusAdjacentTabbable(event.currentTarget, event.shiftKey ? -1 : 1)) {
        event.preventDefault();
      }
      return;
    }
    if (event.key !== 'ArrowDown') return;
    event.preventDefault();
    setIsOpen(true);
    requestAnimationFrame(() => {
      document.getElementById(`group-size-${idSuffix}`)?.focus();
    });
  };

  const handleUpdate = async () => {
    trackWhoUpdate();
    setIsUpdating(true);
    try {
      // Keep the panel open on a failed commit so the pending edit isn't silently lost.
      if ((await commitFulfillment()).ok) {
        setIsOpen(false);
        onUpdateApplied?.();
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOpenChange: PopoverOpenChange = (next, eventDetails) => {
    // Presses inside the anchor container are part of the field itself, so they must not dismiss
    // the popup. Base UI replaces Radix's `onInteractOutside` with a cancelable `onOpenChange`.
    if (eventDetails.reason === 'outside-press') {
      const target = eventDetails.event.target as HTMLElement | null;
      if (target?.closest(`#${containerId}`)) {
        eventDetails.cancel();
        return;
      }
    }

    // Replaces `onEscapeKeyDown`; `finalFocus` below consumes this to restore the input.
    if (eventDetails.reason === 'escape-key') {
      restoringFocusRef.current = true;
    }

    setIsOpen(next);
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <label
        className={twJoin(
          'relative -left-2 flex flex-col justify-center rounded-full border-2 border-transparent py-2 transition-all duration-200 focus-within:z-10 hover:z-10 motion-reduce:transition-none',
          isCompressed ? 'h-[60px] px-5' : 'h-20 px-8',
        )}
        htmlFor={inputId}
        id={containerId}
        ref={anchorRef}
      >
        {!isCompressed && (
          <span className="font-semibold text-black-olive">{t('eventBarV2.who.label')}</span>
        )}
        <TextField.Root className="border-none bg-transparent p-0 focus-within:ring-0 hover:bg-transparent">
          <TextField.Input
            aria-controls={panelId}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            className={twJoin(
              'cursor-pointer truncate p-0 focus:outline-none focus:ring-0',
              isCompressed && 'font-semibold placeholder:text-peppercorn-500',
            )}
            data-testid="who-input"
            id={inputId}
            placeholder={t('eventBarV2.who.placeholder')}
            readOnly
            ref={inputRef}
            value={
              Number.isFinite(headCount)
                ? t('eventBarV2.who.summary.guests', {count: headCount})
                : ''
            }
            onClick={() => {
              setIsOpen(true);
            }}
            onFocus={() => {
              if (restoringFocusRef.current) {
                restoringFocusRef.current = false;
                return;
              }
              setIsOpen(true);
            }}
            onKeyDown={handleKeyDown}
          />
        </TextField.Root>
      </label>
      <Popover.Content
        align="start"
        anchor={anchorRef}
        aria-label={t('eventBarV2.who.label')}
        className="mt-2 max-h-[var(--available-height)] w-80 overflow-y-auto rounded-2xl border border-peppercorn-100 bg-white p-6 shadow-lg"
        data-event-bar-surface
        data-testid="who-suggestions-panel"
        finalFocus={() => {
          // Base UI would return focus to the anchor; restore the field ourselves, and only when
          // the panel closed via Escape - never bounce focus back when the user tabs away.
          if (!restoringFocusRef.current) return false;
          if (document.activeElement === inputRef.current) {
            // Escape was pressed from the input itself - nothing to restore.
            restoringFocusRef.current = false;
            return false;
          }
          // Leaves the guard set; the input's onFocus consumes and clears it.
          return inputRef.current;
        }}
        id={panelId}
        initialFocus={false}
        role="dialog"
        sideOffset={8}
      >
        <div className="flex flex-col items-center justify-center gap-4">
          <GroupSizeSelector idSuffix={idSuffix} />
          <EventName idSuffix={idSuffix} />
          <DietaryFilters idSuffix={idSuffix} />
        </div>
        {isOnMenuPage && (
          <Button
            className="mt-4 w-full"
            data-testid="who-update-button"
            loading={isUpdating}
            onClick={handleUpdate}
          >
            {t('eventBarV2.update')}
          </Button>
        )}
      </Popover.Content>
    </Popover.Root>
  );
};

export default Who;
